// Markdown -> sanitized HTML.
// iOS NoteView renders raw Markdown via WKMarkdownView; on the web we use marked + DOMPurify.
// The iOS pipeline supports KaTeX math + custom image renderers — we keep it simple here:
// math blocks pass through as <code>, images attempt to load from their URL as-is.

import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: false,
  mangle: false,
});

const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_TAGS: ["iframe"],
  ADD_ATTR: [
    "target",
    "rel",
    "allow",
    "allowfullscreen",
    "frameborder",
    "loading",
    "referrerpolicy",
    "sandbox",
    "src",
    "srcdoc",
    "style",
  ],
};

export function renderMarkdown(source) {
  if (typeof source !== "string" || source.length === 0) return "";
  // Strip the [LOADING_IMAGE] placeholder used by iOS while images stream.
  const cleaned = source.replace(/!\[LOADING_IMAGE\]\([^)]*\)/g, "");
  const rawHtml = marked.parse(cleaned);
  return DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG);
}

function stripHtml(input) {
  return input.replace(/<[^>]+>/g, "");
}

export function notePreview(content, maxLines = 5) {
  if (typeof content !== "string" || content.length === 0) return "";
  const stripped = stripHtml(content)
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`{3}[\s\S]*?`{3}/g, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    // Image embeds (`![[name|uuid]]`), source-page markers and TikZ
    // placeholders are for the editor, never for a one-line preview.
    .replace(/!\[\[[^\]]*\]\]/g, "")
    .replace(/\{\{page:\d+(?:@\d+)?\}\}/g, "")
    .replace(/\[?START_TIKZ\][\s\S]*?\[?END_TIKZ\]/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\$\$[\s\S]*?\$\$/g, "")
    .replace(/\$[^$\n]+\$/g, "")
    .replace(/[*_~>]/g, "")
    .trim();
  const lines = stripped.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return lines.slice(0, maxLines).join("\n");
}

export function noteTitle(content) {
  if (typeof content !== "string" || content.length === 0) return "";
  const cleaned = stripHtml(content).trim();
  const headingMatch = cleaned.match(/^\s*#{1,6}\s+(.+?)\s*$/m);
  if (headingMatch && headingMatch[1].trim().length > 0) {
    return headingMatch[1].trim();
  }
  const firstLine = cleaned.split(/\r?\n/).find((l) => l.trim().length > 0);
  return firstLine ? firstLine.trim().slice(0, 120) : "";
}

// ---------------------------------------------------------------------------
// The title a notes list shows for a note, as the phone's rows show it
// (NoteRowWidget.swift `NoteDisplayCache.title(for:)`, with the cleaners of
// ios/Shared/StringCleaning.swift and MarkdownPlainText.swift). A note made
// only of a picture, a formula or a drawing gets an icon and a word instead
// of raw markdown (`^{2^{2}}`) or a blank line.
//
// The character classes are spelled out where Swift's (ICU's) differ from
// JavaScript's: `\w` and `\d` there know every script, `\s` and the trimmed
// sets are not quite the same lists.

// CharacterSet.newlines, one separator per character: "\r\n" counts twice.
// Also what ICU's `.` never matches.
const NEWLINE_CHARS = /[\n\v\f\r\u0085\u2028\u2029]/;
// ICU `\s`.
const ICU_SPACE = /[\t\n\f\r\p{Z}]/u;
const ICU_SPACE_RUN = /[\t\n\f\r\p{Z}]+/gu;
// `.whitespacesAndNewlines` and `.whitespaces`, as trimmed by Swift.
const EDGE_SPACE = /^[\t-\r\u0085\p{Z}]+|[\t-\r\u0085\p{Z}]+$/gu;
const EDGE_BLANKS = /^[\t\p{Zs}]+|[\t\p{Zs}]+$/gu;
// ICU `[\w*]`.
const WORD_OR_STAR = /[\p{Alphabetic}\p{M}\p{Nd}\p{Pc}\p{Join_Control}*]/u;
const MARKDOWN_SENSITIVE = /[<&#*_~`![=$>]/;

const trimSpace = (s) => s.replace(EDGE_SPACE, "");
const trimBlanks = (s) => s.replace(EDGE_BLANKS, "");

/** The list keeps a note's first five lines only (MainView `previewContent`). */
function listContent(content) {
  return content.split(NEWLINE_CHARS).slice(0, 5).join("\n");
}

function replaceEntities(text, entities) {
  let out = text;
  for (const [entity, value] of entities) out = out.split(entity).join(value);
  return out;
}

/** StringCleaning.swift `plainTextFromHTML`. */
function plainTextFromHTML(html) {
  const text = replaceEntities(
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, " "),
    [["&nbsp;", " "], ["&amp;", "&"], ["&lt;", "<"], ["&gt;", ">"], ["&quot;", '"'], ["&#39;", "'"]],
  )
    .replace(/^[ \t]+/gm, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n");
  return trimSpace(text);
}

/**
 * The italic rule of MarkdownPlainText,
 * `(?<![\w*])[*_](?!\s)(.+?)(?<!\s)[*_](?![\w*])` → `$1`, walked by hand:
 * a look-behind in a regular expression keeps older Safari from loading
 * the page at all.
 */
function stripItalics(text) {
  const isMarker = (ch) => ch === "*" || ch === "_";
  const wordAt = (i) => i >= 0 && i < text.length && WORD_OR_STAR.test(text[i]);
  let out = "";
  let from = 0;
  let i = 0;
  while (i < text.length) {
    if (isMarker(text[i]) && !wordAt(i - 1) && i + 1 < text.length && !ICU_SPACE.test(text[i + 1])) {
      let close = -1;
      for (let j = i + 2; j < text.length; j++) {
        if (NEWLINE_CHARS.test(text[j - 1])) break;
        if (isMarker(text[j]) && !ICU_SPACE.test(text[j - 1]) && !wordAt(j + 1)) {
          close = j;
          break;
        }
      }
      if (close > 0) {
        out += text.slice(from, i) + text.slice(i + 1, close);
        from = close + 1;
        i = close + 1;
        continue;
      }
    }
    i += 1;
  }
  return out + text.slice(from);
}

/** MarkdownPlainText.plain: markdown syntax removed, empty lines dropped.
 *  Whole constructs go before the inline markers they may hold. */
function markdownPlain(markdown) {
  const constructs = markdown
    .replace(/\[?START_(TIKZ|WIDGET)\][\s\S]*?\[?END_\1\]/g, "")
    .replace(/\[?LOADING_IMAGE_\d+\]?/g, "")
    .replace(/!\[\[[^\]]*\]\]/g, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]{0,3}>[ \t]?/gm, "")
    .replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+(?:\[[ xX]\][ \t]+)?/gm, "")
    .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, "")
    .replace(/(\*\*|__|==|~~)(.+?)\1/g, "$2");
  return stripItalics(constructs)
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\${1,2}([^$]+?)\${1,2}/g, "$1")
    .replace(/<[^>]+>/g, "")
    .split(NEWLINE_CHARS)
    .map(trimBlanks)
    .filter((line) => line.length > 0)
    .join("\n");
}

/** Two of space, tab, CR or LF in a row (`cleanMarkdown`'s fast path). */
function hasWhitespaceRun(line) {
  let lastWasSpace = false;
  for (const ch of line) {
    const isSpace = ch === " " || ch === "\t" || ch === "\n" || ch === "\r";
    if (isSpace && lastWasSpace) return true;
    lastWasSpace = isSpace;
  }
  return false;
}

/** StringCleaning.swift `cleanMarkdown(line:)`: one line as readable text. */
function cleanMarkdownLine(line) {
  // A list marker is markdown only at the start of the line.
  const leading = line.replace(/^[ \t]+/, "");
  const sensitive = MARKDOWN_SENSITIVE.test(line) || /^(?:[-+] |\p{Nd}+[.)] )/u.test(leading);
  if (!sensitive) {
    return hasWhitespaceRun(line) ? trimSpace(line.replace(ICU_SPACE_RUN, " ")) : trimSpace(line);
  }
  const text = plainTextFromHTML(line) || line;
  return trimSpace(markdownPlain(text).replace(ICU_SPACE_RUN, " "));
}

function replaceIgnoringCase(text, literal, replacement) {
  const escaped = literal.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
  return text.replace(new RegExp(escaped, "gi"), replacement);
}

/** NoteRowWidget.swift `firstNonEmptyLine(from:)`, with its tags and
 *  entities exactly as it lists them. */
function firstNonEmptyLine(text) {
  if (!text.includes("<") && !text.includes("&")) {
    return text.split(/[\n\r]/).map(trimSpace).find((l) => l.length > 0) || "...";
  }
  let s = text;
  for (const tag of ["<br>", "<br/>", "<br />", "</p>", "</div>", "</li>", "</h1>", "</h2>", "<h3>", "</h4>", "</h5>", "</h6>"]) {
    s = replaceIgnoringCase(s, tag, "\n");
  }
  for (const tag of ["<p>", "<div>", "<span>", "<li>", "<ul>", "<ol>", "<h1>", "<h2>", "<h3>", "<h4>", "<h5>", "<h6>"]) {
    s = replaceIgnoringCase(s, tag, "");
  }
  s = replaceEntities(s.replace(/<[^>]+>/g, ""), [
    ["&nbsp;", " "], ["&amp;", "&"], ["&lt;", "<"], ["&gt;", ">"], ["&quot;", '"'], ["&#34;", '"'], ["&#39;", "'"],
  ]);
  return (
    s
      .replace(/[ \t]*\n[ \t]*/g, "\n")
      .split(NEWLINE_CHARS)
      .map(trimSpace)
      .find((l) => l.length > 0) || "..."
  );
}

/** A line that is only an embed or only maths, labelled
 *  (`NoteDisplayCache.mediaTitle`). */
function mediaTitle(line) {
  if (line.startsWith("![widget:")) {
    const end = line.indexOf("]");
    if (end >= 0) {
      const label = line.slice(9, end).replace(/^[$ ]+|[$ ]+$/g, "");
      return label ? { text: label, label: null, symbol: "cube" } : { text: "", label: "image", symbol: "cube" };
    }
  }
  if (line.startsWith("![") && (line.endsWith(")") || line.endsWith("]]"))) {
    return { text: "", label: "image", symbol: "photo" };
  }
  const isMath =
    (line.startsWith("$") && line.endsWith("$")) ||
    (line.startsWith("\\[") && line.endsWith("\\]")) ||
    (line.startsWith("\\(") && line.endsWith("\\)"));
  return isMath ? { text: "", label: "formula", symbol: "function" } : null;
}

/**
 * What a notes list shows for a note, `{ text, label, symbol }`:
 *
 * - its first line of real text: `{ text, label: null, symbol: null }`;
 * - else the first media line met on the way, as a word to translate
 *   (`label` "image" or "formula", `notes.row.<label>`) with `symbol`
 *   "photo" or "function" — a widget gives its own label as `text`, with
 *   symbol "cube";
 * - else, with no text at all, what a text-less note is kept for: a
 *   drawing (`label` "drawing", symbol "scribble").
 */
export function noteRowTitle(content) {
  const source = typeof content === "string" ? listContent(content) : "";
  let fallback = null;
  for (const raw of source.split(/[\n\r]/)) {
    const line = trimSpace(raw);
    if (!line) continue;
    const media = mediaTitle(line);
    if (media) {
      if (!fallback) fallback = media;
      continue;
    }
    const cleaned = cleanMarkdownLine(firstNonEmptyLine(line));
    if (trimBlanks(cleaned)) return { text: cleaned, label: null, symbol: null };
  }
  return fallback || { text: "", label: "drawing", symbol: "scribble" };
}

/**
 * The preview a card shows under `title` (a `noteRowTitle`): the note's
 * first lines without the one already shown as the title. A label
 * ("Image", a widget's name) is not the note's words, so it takes no line.
 */
export function notePreviewUnderTitle(content, title) {
  const fullPreview = notePreview(content, 5);
  const t = title && !title.symbol ? title.text : "";
  if (!t) return fullPreview;
  if (fullPreview.startsWith(t)) return fullPreview.slice(t.length).trim();
  // The preview cleans a line its own way ("- item" keeps its dash,
  // "&amp;" its entity): a first line that reads as the title is it.
  const [first, ...rest] = fullPreview.split("\n");
  return noteRowTitle(first).text === t ? rest.join("\n").trim() : fullPreview;
}
